<template>
	<v-card class="modern-product-card d-flex flex-column" flat outlined :max-width="maxWidth">
		<!-- Image Container with Floating Category & Stock Badges -->
		<div class="product-image-wrap" @click="$emit('click:image', $event)">
			<v-img contain :src="product.image_url" height="180px" class="product-image">
				<template v-slot:placeholder>
					<v-row class="fill-height ma-0" align="center" justify="center">
						<v-progress-circular indeterminate color="grey lighten-3" size="24"></v-progress-circular>
					</v-row>
				</template>
			</v-img>

			<!-- Category Badge (Top Left) -->
			<div class="category-badge-wrap" v-if="product.categoria">
				<span
					class="category-pill text-truncate"
					:style="
						product.categoria.color
							? { backgroundColor: product.categoria.color + '15', color: product.categoria.color }
							: {}
					"
				>
					{{ product.categoria.nombre }}
				</span>
			</div>

			<!-- Featured Badge (Top Right) -->
			<div class="featured-badge-wrap" v-if="product.destacado == '1' || product.destacado === true">
				<span class="featured-pill text-capitalize">
					<v-icon x-small color="#f59e0b" class="mr-1">mdi-star</v-icon>
					{{ "starred" | trans }}
				</span>
			</div>

			<!-- Quick Zoom / View Hint -->
			<div class="quick-view-overlay">
				<v-icon small color="white">mdi-eye</v-icon>
			</div>
		</div>

		<!-- Card Body Content -->
		<div class="pa-4 d-flex flex-column flex-grow-1">
			<!-- Product Title (Clamped to 2 lines for uniform grid) -->
			<h3
				class="product-title mb-2 text-decoration-none text-slate-800"
				:title="product.name"
				@click="$emit('click:image', $event)"
			>
				{{ product.name }}
			</h3>

			<!-- Reference / SKU Subtitle -->
			<div class="product-ref mb-2">
				<span class="caption text-secondary-custom">
					{{ "store.reference" | trans }}: <strong class="text-slate-700">{{ product.referencia }}</strong>
				</span>
			</div>

			<v-spacer></v-spacer>

			<!-- Price & Stock Row -->
			<div class="d-flex align-center justify-space-between my-2">
				<div>
					<div v-if="product.mostrar_precio" class="product-price">
						{{ product | price | money }}
					</div>
					<div v-else class="text-caption text-secondary-custom font-italic">
						{{ "store.consult_price" | trans }}
					</div>
				</div>

				<div class="text-right">
					<span v-if="product.mostrar_stock" class="stock-pill in-stock">
						{{ "store.stock" | trans }}: {{ product.stock | number }}
					</span>
					<span v-else-if="product.stock > 0" class="stock-pill in-stock">
						{{ "store.stock_available" | trans }}
					</span>
				</div>
			</div>

			<!-- Actions -->
			<div class="product-card-actions pt-2 d-flex align-center">
				<v-btn
					v-if="!product.advanced_stock || product.advanced_stock.length == 0"
					color="primary"
					depressed
					rounded
					small
					class="flex-grow-1 mr-2 add-cart-btn text-capitalize"
					@click="addToCart"
				>
					<v-icon left x-small>mdi-cart-plus</v-icon>
					<span>{{ "store.add" | trans }}</span>
				</v-btn>

				<v-btn
					:to="`/product/${encodeURIComponent(product.referencia)}`"
					color="primary"
					outlined
					rounded
					small
					:class="
						!product.advanced_stock || product.advanced_stock.length == 0
							? 'details-btn-icon'
							: 'flex-grow-1 text-capitalize'
					"
				>
					<template v-if="!product.advanced_stock || product.advanced_stock.length == 0">
						<v-icon small>mdi-arrow-right</v-icon>
					</template>
					<template v-else>
						<v-icon left x-small>mdi-tune</v-icon>
						<span>{{ "options" | trans }}</span>
					</template>
				</v-btn>
			</div>
		</div>
	</v-card>
</template>

<script>
export default {
	name: "ProductCard",
	props: ["product", "maxWidth"],
	methods: {
		addToCart() {
			this.api.cart.addToCart(this.product);
		},
	},
};
</script>

<style scoped>
.modern-product-card {
	background-color: #ffffff !important;
	border: 1px solid #eef2f6 !important;
	border-radius: 14px !important;
	overflow: hidden;
	height: 380px;
	transition: transform 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s cubic-bezier(0.16, 1, 0.3, 1),
		border-color 0.2s ease;
	position: relative;
}

.modern-product-card:hover {
	transform: translateY(-5px);
	box-shadow: 0 16px 32px -8px rgba(15, 23, 42, 0.09), 0 4px 12px -2px rgba(15, 23, 42, 0.04) !important;
	border-color: #cbd5e1 !important;
}

/* Image Container */
.product-image-wrap {
	position: relative;
	background-color: #f8fafc;
	border-bottom: 1px solid #f1f5f9;
	padding: 12px;
	cursor: pointer;
	overflow: hidden;
}

.product-image {
	transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

.product-image-wrap:hover .product-image {
	transform: scale(1.06);
}

/* Quick View Overlay */
.quick-view-overlay {
	position: absolute;
	right: 10px;
	bottom: 10px;
	background: rgba(15, 23, 42, 0.65);
	backdrop-filter: blur(4px);
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.2s ease, transform 0.2s ease;
	transform: scale(0.9);
}

.product-image-wrap:hover .quick-view-overlay {
	opacity: 1;
	transform: scale(1);
}

/* Category Badge */
.category-badge-wrap {
	position: absolute;
	top: 10px;
	left: 10px;
	z-index: 2;
	max-width: 60%;
}

.category-pill {
	display: inline-block;
	font-size: 0.72rem;
	font-weight: 600;
	padding: 3px 8px;
	border-radius: 6px;
	background-color: #eff6ff;
	color: #2563eb;
	letter-spacing: 0.01em;
	max-width: 100%;
}

/* Featured Badge */
.featured-badge-wrap {
	position: absolute;
	top: 10px;
	right: 10px;
	z-index: 2;
}

.featured-pill {
	display: inline-flex;
	align-items: center;
	font-size: 0.7rem;
	font-weight: 700;
	padding: 2px 7px;
	border-radius: 6px;
	background-color: #fffbeb;
	color: #b45309;
	border: 1px solid #fef3c7;
	letter-spacing: 0.01em;
}

/* Title & Meta */
.product-title {
	font-size: 0.95rem;
	font-weight: 600;
	line-height: 1.35;
	color: #0f172a;
	cursor: pointer;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	height: 2.7em;
	transition: color 0.15s ease;
}

.product-title:hover {
	color: #2563eb;
}

.text-slate-800 {
	color: #1e293b;
}

.text-slate-700 {
	color: #334155;
}

.text-secondary-custom {
	color: #64748b;
}

.product-price {
	font-size: 1.125rem;
	font-weight: 700;
	color: #0f172a;
	letter-spacing: -0.01em;
}

.stock-pill {
	font-size: 0.72rem;
	font-weight: 600;
	padding: 3px 8px;
	border-radius: 6px;
	background-color: #f1f5f9;
	color: #475569;
}

.stock-pill.in-stock {
	background-color: #ecfdf5;
	color: #059669;
}

/* Actions */
.product-card-actions {
	border-top: 1px solid #f8fafc;
}

.add-cart-btn {
	font-weight: 600;
	letter-spacing: 0;
}

.details-btn-icon {
	min-width: 32px !important;
	padding: 0 8px !important;
}
</style>
